import { Directive, Input, HostListener, Renderer2, ElementRef } from '@angular/core';
import { SmartRoundPipe } from 'src/app/pipes/smart-round/smart-round.pipe';
import { FestoSpecial } from 'src/app/service-components/specials/festo-specials/festo-special';
import { INDISpecial } from 'src/app/service-components/specials/indi-specials/indi-special';
import { KrampSpecial } from 'src/app/service-components/specials/kramp-specials/kramp-special';
import { TUSpecial } from 'src/app/service-components/specials/tu-specials/tu-special';
import { showCopiedPopup, writeToClipboard } from './clipboard';

export type Special = KrampSpecial | FestoSpecial | INDISpecial | TUSpecial;

export interface SpecialField {
  label: string;
  value: string;
}

const SEPARATOR = '――――――――――――――――――――――――――――――――――――――――――――――――――';

const CART_SENTENCE_SINGLE =
  'Ik heb dit artikel alvast in je winkelwagen gezet, zo kun je het eenvoudig samen met andere onderdelen bestellen.';
const CART_SENTENCE_MULTIPLE =
  'Ik heb deze artikelen alvast in je winkelwagen gezet, zo kun je ze eenvoudig samen met andere onderdelen bestellen.';

@Directive({
  selector: '[copySpecials]',
})
export class CopySpecialDirective {
  @Input() specials: KrampSpecial[] | FestoSpecial[] | INDISpecial[] | TUSpecial[] = [];
  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private smartRoundPipe: SmartRoundPipe
  ) {}

  @HostListener('click')
  async copy() {
    if (!this.specials.length) {
      return;
    }
    const now = new Date();
    const single = this.specials.length === 1;
    const greet = now.getHours() < 12 ? 'Goedemorgen' : 'Goedemiddag';
    const offer = single ? 'aanbieding' : 'aanbiedingen';
    const part = single ? 'het gevraagde onderdeel' : 'de gevraagde onderdelen';
    const cart = single ? CART_SENTENCE_SINGLE : CART_SENTENCE_MULTIPLE;
    const intro = `Bedankt voor je aanvraag. Hierbij de ${offer} voor ${part}, deze kunnen we leveren als special:`;

    const blocks = (this.specials as Special[]).map(getSpecialFields);

    const plainBlocks = blocks
      .map(fields => fields.map(field => `${field.label}: ${field.value}`).join('\n'))
      .join(`\n${SEPARATOR}\n`);

    const plain = `${greet},\n\n${intro}\n\n${plainBlocks}\n\n${cart}`;

    const htmlItems = blocks
      .map(
        fields => `<ul style="list-style: none; padding: 0; margin: 0;">
          ${fields.map(field => `<li><strong>${field.label}:</strong> ${field.value}</li>`).join('\n          ')}
        </ul>`
      )
      .join(`<p>${SEPARATOR}</p>`);

    const html = `
      <p>${greet},</p>
      <p>${intro}</p>
      ${htmlItems}
      <p>${cart}</p>
    `;

    await writeToClipboard({ plain, html });
    this.showPopup();
  }

  showPopup(): void {
    showCopiedPopup(this.renderer, this.el.nativeElement);
  }
}

export const getSpecialFields = (special: Special): SpecialField[] => {
  const fields: SpecialField[] = [
    { label: 'Artikel', value: special.sku },
    { label: 'Omschrijving', value: special.description },
    { label: 'Prijs', value: `€ ${formatPrice(special.salesPrice)} netto per stuk` },
    { label: 'Levertijd', value: `ca. ${getWeekText(special.deliveryDays)} na opdracht` },
  ];
  if (special.orderUnit > 1) {
    fields.push({ label: 'Besteleenheid', value: `per ${special.orderUnit.toString()} stuks` });
  }
  return fields;
};

export const getWeekText = (deliveryDays: number) => {
  const w = Math.max(1, Math.ceil(deliveryDays / 7));
  const weekText = w > 1 ? `${w.toString()} weken` : `${w.toString()} week`;
  return weekText;
};

export const formatPrice = (salesPrice: number | undefined) => {
  if (salesPrice === undefined || isNaN(salesPrice)) {
    return 'onbekend';
  }
  const pipe = new SmartRoundPipe();
  return pipe.transform(salesPrice) ?? 'onbekend';
};
